import { useEffect, useState } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import {
  getUnit,
  getLessonsWithProgress,
  getWorkshopByUnit,
  getUnitExam,
  syncStudentProgress
} from '@/lib/db'
import type {
  Unit,
  LessonWithProgress,
  UnitWorkshop,
  UnitExam,
  LessonStatus
} from '@/types'
import {
  ArrowLeft,
  CheckCircle,
  Lock,
  PlayCircle,
  Calendar,
  FileText,
  Clock,
  Target,
  BookOpen,
  ChevronRight,
  Video,
  AlertTriangle,
  X,
  Trophy,
  XCircle
} from 'lucide-react'

interface ExamAttemptStatus {
  hasTaken: boolean
  passed: boolean
  bestScore: number
  attempts: number
}

const statusColors: Record<LessonStatus, { bg: string; text: string; border: string }> = {
  locked: { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-300' },
  available: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },
  in_progress: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' }
}

const statusLabels: Record<LessonStatus, string> = {
  locked: 'Bloqueada',
  available: 'Disponible',
  in_progress: 'En Progreso',
  completed: 'Completada'
}

const statusIcons: Record<LessonStatus, React.ReactNode> = {
  locked: <Lock className="w-5 h-5" />,
  available: <PlayCircle className="w-5 h-5" />,
  in_progress: <PlayCircle className="w-5 h-5" />,
  completed: <CheckCircle className="w-5 h-5" />
}

interface ExamResultState {
  passed: boolean
  score: number
  justFinished: boolean
}

export default function UnitDetail() {
  const { unitId } = useParams<{ unitId: string }>()
  const { profile, student, isInCohort } = useAuth()
  const location = useLocation()

  // Obtener resultado del examen si viene de la página del examen
  const examResultFromNav = (location.state as { examResult?: ExamResultState })?.examResult

  console.log('📍 UnitDetail - location.state:', location.state)
  console.log('📍 UnitDetail - examResultFromNav:', examResultFromNav)

  const [unit, setUnit] = useState<Unit | null>(null)
  const [lessons, setLessons] = useState<LessonWithProgress[]>([])
  const [workshop, setWorkshop] = useState<UnitWorkshop | null>(null)
  const [exam, setExam] = useState<UnitExam | null>(null)
  const [examStatus, setExamStatus] = useState<ExamAttemptStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Estados para modales del taller
  const [showNotAvailableModal, setShowNotAvailableModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [workshopAttended, setWorkshopAttended] = useState(false)

  // Estado para mostrar resultado del examen recién terminado
  const [showExamResult, setShowExamResult] = useState(false)
  const [examResultData, setExamResultData] = useState<ExamResultState | null>(null)

  // Manejar resultado del examen si viene de la página del examen (PRIMERO)
  useEffect(() => {
    if (examResultFromNav?.justFinished) {
      console.log('📊 Resultado del examen recibido:', examResultFromNav)
      setExamResultData(examResultFromNav)
      setShowExamResult(true)
      // Actualizar el estado del examen inmediatamente
      setExamStatus({
        hasTaken: true,
        passed: examResultFromNav.passed,
        bestScore: examResultFromNav.score,
        attempts: 1
      })
    }
  }, []) // Solo al montar

  useEffect(() => {
    if (unitId && student) {
      loadUnitData()
    }
  }, [unitId, student])

  const loadUnitData = async () => {
    if (!unitId || !student) return

    setIsLoading(true)

    // Cargar datos de la unidad
    const { data: unitData } = await getUnit(unitId)
    if (unitData) {
      setUnit(unitData)
    }

    // Sync progress first (fixes stale lesson/unit counts)
    await syncStudentProgress(student.id)

    // Cargar lecciones con progreso (student.id = PK of students table)
    const { data: lessonsData } = await getLessonsWithProgress(student.id, unitId)
    if (lessonsData) {
      setLessons(lessonsData)
    }

    // Cargar taller (si existe la tabla)
    try {
      const { data: workshopData } = await getWorkshopByUnit(unitId)
      if (workshopData) {
        setWorkshop(workshopData)
        // Cargar estado de asistencia del estudiante para este taller
        const { data: attData } = await (supabase
          .from('workshop_attendance') as any)
          .select('status')
          .eq('workshop_id', workshopData.id)
          .eq('student_id', student.id)
          .maybeSingle()
        if (attData?.status === 'attended') {
          setWorkshopAttended(true)
        }
      }
    } catch (e) {
      // Tabla no existe todavía, ignorar
    }

    // Cargar examen (si existe la tabla)
    try {
      const { data: examData } = await getUnitExam(unitId)
      if (examData) {
        setExam(examData)

        // Solo cargar de la BD si NO viene de terminar el examen
        // (el useEffect ya estableció examStatus si viene del examen)
        if (!examResultFromNav?.justFinished) {
          // Cargar estado de intentos del examen desde la BD
          const { data: attemptsData } = await (supabase
            .from('exam_attempts') as any)
            .select('*')
            .eq('exam_id', examData.id)
            .eq('student_id', student!.id)

          if (attemptsData && attemptsData.length > 0) {
            const bestAttempt = attemptsData.reduce((prev: any, current: any) =>
              (current.score > prev.score) ? current : prev
            )
            setExamStatus({
              hasTaken: true,
              passed: attemptsData.some((a: any) => a.passed),
              bestScore: bestAttempt.score,
              attempts: attemptsData.length
            })
          } else {
            setExamStatus({
              hasTaken: false,
              passed: false,
              bestScore: 0,
              attempts: 0
            })
          }
        } else {
          console.log('📊 Usando resultado de navegación, no consultar BD')
          // Limpiar el state de navegación después de un momento
          setTimeout(() => {
            window.history.replaceState({}, document.title)
          }, 100)
        }
      }
    } catch (e) {
      // Tabla no existe todavía, ignorar
      console.log('Error cargando examen:', e)
    }

    setIsLoading(false)
  }

  // Verificar si el taller está disponible (10 minutos antes hasta que termine)
  const isWorkshopAvailable = () => {
    if (!workshop) return false

    const now = new Date()
    const workshopDate = new Date(workshop.scheduled_date)
    const tenMinutesBefore = new Date(workshopDate.getTime() - 10 * 60 * 1000)
    const workshopEnd = new Date(workshopDate.getTime() + 60 * 60 * 1000)

    return now >= tenMinutesBefore && now <= workshopEnd
  }

  // Formatear fecha y hora del taller
  const formatWorkshopDateTime = () => {
    if (!workshop) return ''
    const date = new Date(workshop.scheduled_date)
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Manejar clic en "Ir al taller"
  const handleWorkshopClick = () => {
    // Si ya asistió, abrir el link directamente sin modal
    if (workshopAttended) {
      if (workshop?.meeting_url) {
        window.open(workshop.meeting_url, '_blank', 'noopener,noreferrer')
      } else {
        alert('No hay un enlace configurado para este taller.')
      }
      return
    }

    if (isWorkshopAvailable()) {
      setShowConfirmModal(true)
    } else {
      setShowNotAvailableModal(true)
    }
  }

  // Registrar asistencia y abrir el taller
  const handleConfirmAttendance = async () => {
    if (!workshop || !profile) return

    setIsRegistering(true)

    try {
      // Primero verificar si ya existe un registro
      const { data: existing } = await (supabase
        .from('workshop_attendance') as any)
        .select('id')
        .eq('workshop_id', workshop.id)
        .eq('student_id', student!.id)
        .maybeSingle()

      if (existing) {
        // Ya existe, actualizar
        await (supabase
          .from('workshop_attendance') as any)
          .update({
            status: 'attended',
            attended_at: new Date().toISOString()
          })
          .eq('id', existing.id)
      } else {
        // No existe, insertar
        const { error } = await (supabase
          .from('workshop_attendance') as any)
          .insert({
            workshop_id: workshop.id,
            student_id: student!.id,
            status: 'attended',
            attended_at: new Date().toISOString()
          })

        if (error) {
          console.error('Error registrando asistencia:', error)
          // Si es error de tabla no existe, mostrar mensaje
          if (error.code === '42P01') {
            alert('La tabla de asistencia no existe. Contacta al administrador.')
            setIsRegistering(false)
            return
          }
        }
      }

      // Marcar como asistido en el estado local
      setWorkshopAttended(true)

      // Abrir el link del taller en una nueva ventana
      if (workshop.meeting_url) {
        window.open(workshop.meeting_url, '_blank', 'noopener,noreferrer')
      } else {
        alert('No hay un enlace configurado para este taller.')
      }

      setShowConfirmModal(false)
    } catch (error) {
      console.error('Error:', error)
      alert('Hubo un error al registrar la asistencia.')
    } finally {
      setIsRegistering(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  if (!unit) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Unidad no encontrada</p>
        <Link to="/units" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
          Volver a unidades
        </Link>
      </div>
    )
  }

  const isPremium = student?.subscription_status === 'active'
  const hasFullAccess = isPremium && isInCohort
  const isPaidWaiting = isPremium && !isInCohort
  const isUnitFree = unit?.order_num === 1
  const completedLessons = lessons.filter(l => l.progress?.status === 'completed').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/units"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <span className="text-sm text-blue-600 font-medium">
            Unidad {unit.order_num}
          </span>
          <h1 className="text-2xl font-bold text-gray-900">{unit.title}</h1>
        </div>
      </div>

      {/* Unit Info */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <p className="text-gray-600 mb-4">{unit.description || 'Sin descripción'}</p>

        {/* Objetivos */}
        {unit.objectives && unit.objectives.length > 0 && (
          <div className="mb-4">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Objetivos
            </h3>
            <ul className="space-y-1">
              {unit.objectives.map((objective: string, index: number) => (
                <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {objective}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Progress */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Progreso de la unidad</span>
            <span className="text-sm font-medium">{completedLessons} / {lessons.length} lecciones</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${lessons.length > 0 ? (completedLessons / lessons.length) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Lessons */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Lecciones
        </h2>

        <div className="space-y-3">
          {lessons.map((lesson, index) => {
            const isContentLocked = !hasFullAccess && !(isUnitFree && index === 0)
            const status = isContentLocked ? 'locked' : (lesson.progress?.status || 'locked')
            const colors = statusColors[status]
            const isLocked = status === 'locked'

            return (
              <div
                key={lesson.id}
                className={`rounded-xl border-2 overflow-hidden transition-all ${colors.border} ${colors.bg} ${
                  isLocked ? 'opacity-60' : 'hover:shadow-md'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${colors.bg}`}>
                      {isContentLocked ? (isPaidWaiting ? <Clock className="w-5 h-5 text-blue-400" /> : <Lock className="w-5 h-5 text-gray-400" />) : statusIcons[status]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-xs text-gray-500">Lección {index + 1}</span>
                          <h3 className="font-medium text-gray-900">{lesson.title}</h3>
                        </div>
                        <div className="flex-shrink-0">
                          {isContentLocked ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${isPaidWaiting ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                              {isPaidWaiting ? 'Próximamente' : 'Premium'}
                            </span>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colors.bg} ${colors.text}`}>
                              {statusLabels[status]}
                            </span>
                          )}
                        </div>
                      </div>
                      {lesson.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{lesson.description}</p>
                      )}
                      {!isContentLocked && !isLocked && (
                        <Link
                          to={`/lesson/${lesson.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 mt-3 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          {status === 'completed' ? 'Repasar' : status === 'in_progress' ? 'Continuar' : 'Comenzar'}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Progress bar for in_progress lessons */}
                  {status === 'in_progress' && lesson.progress && !isContentLocked && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Contenidos completados</span>
                        <span className="font-medium">{lesson.progress.contents_completed} / {lesson.progress.total_contents || 4}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-yellow-500 rounded-full"
                          style={{ width: `${(lesson.progress.contents_completed / (lesson.progress.total_contents || 4)) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {lessons.length === 0 && (
            <div className="text-center py-8 bg-white rounded-xl border">
              <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No hay lecciones disponibles</p>
            </div>
          )}
        </div>
      </div>

      {/* Workshop */}
      {workshop && (
        <div className={`rounded-xl p-4 sm:p-6 border-2 ${hasFullAccess ? 'bg-purple-50 border-purple-200' : isPaidWaiting ? 'bg-blue-50 border-blue-200 opacity-75' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
          <div className="flex items-start gap-3 sm:gap-4">
            <div className={`p-3 rounded-lg flex-shrink-0 ${hasFullAccess ? 'bg-purple-100' : isPaidWaiting ? 'bg-blue-100' : 'bg-gray-200'}`}>
              {hasFullAccess ? <Video className="w-6 h-6 text-purple-600" /> : isPaidWaiting ? <Clock className="w-6 h-6 text-blue-500" /> : <Lock className="w-6 h-6 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-900">Taller en Vivo: {workshop.title}</h3>
                {!hasFullAccess && (
                  <span className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${isPaidWaiting ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                    {isPaidWaiting ? 'Próximamente' : 'Premium'}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">{workshop.description}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  {new Date(workshop.scheduled_date).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long'
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  {new Date(workshop.scheduled_date).toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  {workshop.duration_minutes} min
                </span>
              </div>
              {hasFullAccess && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {workshopAttended && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Asistido
                    </div>
                  )}
                  <button
                    onClick={handleWorkshopClick}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isWorkshopAvailable()
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-purple-200 text-purple-700 hover:bg-purple-300'
                    }`}
                  >
                    Ir al Taller
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Exam */}
      {exam && (
        <div className={`rounded-xl overflow-hidden border-2 ${!hasFullAccess ? (isPaidWaiting ? 'bg-blue-50 border-blue-200 opacity-75' : 'bg-gray-50 border-gray-200 opacity-60') :
          examStatus?.passed
            ? 'bg-green-50 border-green-300'
            : examStatus?.hasTaken
              ? 'bg-red-50 border-red-300'
              : 'bg-orange-50 border-orange-200'
        }`}>
          {/* Banner de resultado si ya rindió */}
          {hasFullAccess && examStatus?.hasTaken && (
            <div className={`px-6 py-3 ${
              examStatus.passed
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {examStatus.passed ? (
                    <Trophy className="w-6 h-6" />
                  ) : (
                    <XCircle className="w-6 h-6" />
                  )}
                  <span className="text-lg font-bold">
                    {examStatus.passed ? 'APROBADO' : 'REPROBADO'} CON {examStatus.bestScore}%
                  </span>
                </div>
                <span className="text-sm opacity-90">
                  {examStatus.attempts} intento{examStatus.attempts > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}

          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className={`p-3 rounded-lg flex-shrink-0 ${!hasFullAccess ? (isPaidWaiting ? 'bg-blue-100' : 'bg-gray-200') :
                examStatus?.passed
                  ? 'bg-green-100'
                  : examStatus?.hasTaken
                    ? 'bg-red-100'
                    : 'bg-orange-100'
              }`}>
                {!hasFullAccess ? (
                  isPaidWaiting ? <Clock className="w-6 h-6 text-blue-500" /> : <Lock className="w-6 h-6 text-gray-400" />
                ) : examStatus?.passed ? (
                  <Trophy className="w-6 h-6 text-green-600" />
                ) : examStatus?.hasTaken ? (
                  <XCircle className="w-6 h-6 text-red-600" />
                ) : (
                  <FileText className="w-6 h-6 text-orange-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-gray-900">Examen: {exam.title}</h3>
                  {!hasFullAccess && (
                    <span className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${isPaidWaiting ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                      {isPaidWaiting ? 'Próximamente' : 'Premium'}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{exam.description}</p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-gray-500">
                  {hasFullAccess && examStatus?.hasTaken ? (
                    <>
                      <span>Puntaje mínimo: {exam.passing_score}%</span>
                      {!examStatus.passed && examStatus.attempts < exam.max_attempts && (
                        <span className="text-orange-600">
                          Te quedan {exam.max_attempts - examStatus.attempts} intento{exam.max_attempts - examStatus.attempts > 1 ? 's' : ''}
                        </span>
                      )}
                    </>
                  ) : hasFullAccess ? (
                    <>
                      <span>Puntuacion minima: {exam.passing_score}%</span>
                      <span>Tiempo limite: {exam.time_limit} min</span>
                    </>
                  ) : null}
                </div>
                {hasFullAccess && (
                  <div className="mt-3">
                    {examStatus?.passed ? (
                      <div className="inline-flex items-center gap-1.5 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">Completado</span>
                      </div>
                    ) : completedLessons < lessons.length ? (
                      <div className="inline-flex items-center gap-1.5 text-gray-500">
                        <Lock className="w-4 h-4" />
                        <span className="text-xs font-medium">Completa las {lessons.length} lecciones primero</span>
                      </div>
                    ) : (
                      <Link
                        to={`/unit/${unitId}/exam`}
                        className="inline-flex items-center gap-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm"
                      >
                        {examStatus?.hasTaken ? 'Reintentar' : 'Ir al examen'}
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Taller no disponible */}
      {showNotAvailableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-yellow-100">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Taller no disponible</h3>
              </div>
              <button
                onClick={() => setShowNotAvailableModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                El link de acceso al taller solo estara disponible <strong>10 minutos antes</strong> del horario programado.
              </p>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-800 font-medium">Fecha y hora del taller:</p>
                <p className="text-purple-900 font-semibold mt-1">{formatWorkshopDateTime()}</p>
              </div>
            </div>
            <button
              onClick={() => setShowNotAvailableModal(false)}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Modal: Resultado del examen */}
      {showExamResult && examResultData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl text-center ${
            examResultData.passed ? 'bg-green-50' : 'bg-red-50'
          }`}>
            <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
              examResultData.passed ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {examResultData.passed ? (
                <Trophy className="w-12 h-12 text-green-600" />
              ) : (
                <XCircle className="w-12 h-12 text-red-600" />
              )}
            </div>

            <h2 className={`text-3xl font-bold mb-2 ${
              examResultData.passed ? 'text-green-800' : 'text-red-800'
            }`}>
              {examResultData.passed ? '¡APROBADO!' : 'REPROBADO'}
            </h2>

            <div className={`text-6xl font-bold my-6 ${
              examResultData.passed ? 'text-green-600' : 'text-red-600'
            }`}>
              {examResultData.score}%
            </div>

            <p className={`text-lg mb-6 ${
              examResultData.passed ? 'text-green-700' : 'text-red-700'
            }`}>
              {examResultData.passed
                ? '¡Felicitaciones! Has aprobado el examen de esta unidad.'
                : 'No alcanzaste el puntaje mínimo requerido. Puedes intentarlo de nuevo.'}
            </p>

            <button
              onClick={() => setShowExamResult(false)}
              className={`w-full py-3 rounded-lg font-semibold text-white transition-colors ${
                examResultData.passed
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Modal: Confirmacion de asistencia */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <Video className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Confirmar Asistencia
              </h2>
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-6">
                <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                <p className="text-lg font-semibold text-gray-900 mb-2">
                  IMPORTANTE
                </p>
                <p className="text-gray-700">
                  Si usted avanza al siguiente paso, se registrará su asistencia como <strong>"ASISTIDO"</strong> al taller.
                </p>
                <p className="text-gray-700 mt-3 font-medium">
                  ¿Seguro que desea avanzar?
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                disabled={isRegistering}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmAttendance}
                disabled={isRegistering}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
              >
                {isRegistering ? 'Registrando...' : 'Si, Ir al Taller'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
